import { ApolloQueryResult, useLazyQuery, useMutation } from "@apollo/client";
import { clientCentralApiStrawberry, clientNeoAuth } from "@neonumy/api-clients";
import * as Sentry from "@sentry/browser";
import { Form } from "antd5";
import { LIST_COMPANIES_FOR_SELECT } from "queries/centralapi/companies";
import {
  GET_USER_ACCESS_COMPANIES_QUERY,
  UPDATE_USER_COMPANIES_PERMISSIONS,
} from "queries/neoauth/users";
import { useEffect, useRef, useState } from "react";
import { onErrorGraphql } from "utils/onErrorGraphql";

interface UserAccessFormValues {
  accountingFirm: { value: ID; label?: string };
  companies?: string[];
}

interface UseCreateUpdateUserAccessParams {
  open: boolean;
  closeModal: () => void;
  userId: ID;
  accountingFirmId?: ID;
  refetch: (
    variables?: Partial<ListAccountingFirmsAccessInput> | undefined,
  ) => Promise<ApolloQueryResult<ListAccountingFirmsAccessOutput>>;
}

export function useCreateUpdateUserAccess({
  open,
  closeModal,
  userId,
  accountingFirmId,
  refetch,
}: UseCreateUpdateUserAccessParams) {
  const [form] = Form.useForm<UserAccessFormValues>();
  const isSelectionSorted = useRef(false);
  const companiesSelect = Form.useWatch("companies", form);
  const accountingFirm = Form.useWatch("accountingFirm", form);

  const [companiesOptions, setCompaniesOptions] = useState<CompanyOptionType[]>([]);

  const [listCompanies, { data: companiesData, error: companiesError }] = useLazyQuery<
    ListCompaniesOptionsOutput,
    ListCompaniesOptionsInput
  >(LIST_COMPANIES_FOR_SELECT, {
    client: clientCentralApiStrawberry,
  });

  useEffect(() => {
    if (companiesData) {
      const companies = companiesData.listCompaniesPaginated?.results
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          key: String(item.id),
          title: item.name,
          internalReference: item?.internalReference,
        }));
      setCompaniesOptions(companies);
    }
  }, [companiesData]);

  useEffect(() => {
    if (companiesError) onErrorGraphql(companiesError);
  }, [companiesError]);

  const [listAccountingFirmsAccess, { data: accessData, error: accessError }] = useLazyQuery<
    ListAccountingFirmsAccessOutput,
    ListAccountingFirmsAccessInput
  >(GET_USER_ACCESS_COMPANIES_QUERY, {
    client: clientNeoAuth,
  });

  useEffect(() => {
    if (accessData) {
      const { getUserAccesses } = accessData;
      if (
        getUserAccesses?.accountingFirms?.length === undefined ||
        getUserAccesses?.accountingFirms?.length === 0
      ) {
        Sentry.captureException(new Error("Invalid accounting firm access"), {
          extra: {
            response: getUserAccesses,
            accountingFirm: getUserAccesses?.accountingFirms,
          },
        });
      } else {
        form.setFieldsValue({
          accountingFirm: {
            label: getUserAccesses.accountingFirms[0].name,
            value: getUserAccesses.accountingFirms[0].id,
          },
          companies: getUserAccesses?.accountingFirms[0]?.companiesList?.map(id => String(id)),
        });
      }
    }
  }, [accessData]);

  useEffect(() => {
    if (accessError) onErrorGraphql(accessError);
  }, [accessError]);

  const [updateCompanies, { loading: isSaving, error: updateError }] = useMutation<
    UpdateUserCompaniesMutationOutput,
    UpdateUserCompaniesMutationInput
  >(UPDATE_USER_COMPANIES_PERMISSIONS, {
    client: clientNeoAuth,
  });

  useEffect(() => {
    if (updateError) onErrorGraphql(updateError);
  }, [updateError]);

  // Fields are filled when the modal opens, not on mount: the parent keeps it mounted.
  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setCompaniesOptions([]);
    isSelectionSorted.current = false;
    if (!accountingFirmId) return;

    listAccountingFirmsAccess({ variables: { accountingFirmId, id: userId } });
  }, [open, accountingFirmId, userId]);

  // Editing has the firm from the start; adding takes it from the field the user picks.
  const selectedAccountingFirmId = accountingFirmId ?? accountingFirm?.value;

  useEffect(() => {
    if (!open || !selectedAccountingFirmId) return;

    listCompanies({
      variables: { filter: { accountingFirm: selectedAccountingFirmId, deletedAt_Isnull: true } },
    });
  }, [open, selectedAccountingFirmId]);

  useEffect(() => {
    if (companiesSelect && companiesOptions.length > 0 && !isSelectionSorted.current) {
      const companies = companiesOptions
        .filter(item => companiesSelect.includes(item.key))
        .sort((a, b) => {
          if (a.title > b.title) return 1;
          if (a.title < b.title) return -1;
          return 0;
        })
        .map(item => item.key);
      form.setFieldValue("companies", companies);
      isSelectionSorted.current = true;
    }
  }, [companiesOptions, companiesSelect]);

  const onFinish = async (values: UserAccessFormValues) => {
    const removeCompanies = companiesOptions
      .map(item => item.key)
      .filter(item => !values.companies?.includes(item));

    const response = await updateCompanies({
      variables: {
        userId,
        addAccountingFirms: values.accountingFirm.value ? [values.accountingFirm.value] : [],
        addCompanies: values.companies,
        removeCompanies: removeCompanies,
      },
    }).catch(() => null);
    if (!response?.data) return;

    refetch();
    closeModal();
  };

  return { form, companiesOptions, isSaving, onFinish };
}
